import { formatDate, formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/settings/conflict-of-interest.tsx
 * @description 利益迴避管理頁面（Task 71）。
 *
 * 功能：
 * - 列表查詢（auditorId / clientId / reason / createdAt）
 * - 新增/編輯利益迴避記錄
 * - 軟刪除
 *
 * 序54：auditorId 改「稽核員」關聯選擇器（AuditorSelect，從員工檔案搜尋勾選）；
 *      clientId 改「客戶」關聯選擇器（ClientSelect，從組織客戶檔案搜尋勾選）；
 *      列表 join 顯示稽核員姓名 + 客戶名稱，前端可按姓名/客戶名搜尋。
 * 視覺：ListPageShell + SectionCard + DataFieldGrid
 */

import { createFileRoute } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import { Button, Col, Form, Input, Modal, Row, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Edit3, ShieldAlert, Search, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, FilterBar } from '@/components/shell';
import ClientSelect from '@/components/common/ClientSelect';
import AuditorSelect from '@/components/common/AuditorSelect';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/conflict-of-interest')({
  component: ConflictOfInterestPage,
});

/**
 * 利益迴避記錄實體類型（對應後端 conflictOfInterest 表）。
 */
interface ConflictOfInterest {
  id: number;
  auditorId: number;
  clientId: number;
  reason: string;
  createdAt: string;
  /** 序54：後端 join 出的稽核員姓名（可能為 null，如帳號已刪） */
  auditorName?: string | null;
  /** 序54：後端 join 出的客戶名稱（可能為 null） */
  clientName?: string | null;
}

/** 後端分頁回應結構 */
interface COIListResponse {
  items: ConflictOfInterest[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 利益迴避管理頁面 */
function ConflictOfInterestPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCOI, setEditingCOI] = useState<ConflictOfInterest | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  /**
   * 查詢利益迴避列表（一次性拉回，前端按稽核員姓名/客戶名/原因過濾）。
   * 後端列表已 join auditorName/clientName，搜尋 UX 直接在前端完成。
   */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-conflict-of-interest'],
    queryFn: () =>
      apiClient.get<COIListResponse>('/api/settings/conflict-of-interest?pageSize=100'),
  });

  const allItems = useMemo(() => data?.items ?? [], [data]);

  /** 前端過濾：按稽核員姓名 / 客戶名稱 / 迴避原因（不分大小寫） */
  const items = useMemo(() => {
    const kw = searchText.trim().toLowerCase();
    if (!kw) return allItems;
    return allItems.filter((it) =>
      [it.auditorName, it.clientName, it.reason]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(kw)),
    );
  }, [allItems, searchText]);

  const total = items.length;

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingCOI(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: ConflictOfInterest) => {
    setEditingCOI(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  /** 表格欄位 */
  const columns: ColumnsType<ConflictOfInterest> = [
    {
      title: '稽核員',
      dataIndex: 'auditorName',
      width: 160,
      render: (v: string | null, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: color.text.primary, fontWeight: weight.medium }}>
            {v ?? '（未知稽核員）'}
          </span>
          <Tag
            style={{ margin: 0, fontFamily: font.mono, fontSize: text.xs }}
            color={color.brand.primary}
          >
            #{record.auditorId}
          </Tag>
        </span>
      ),
    },
    {
      title: '客戶',
      dataIndex: 'clientName',
      width: 200,
      render: (v: string | null, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: color.text.primary }}>{v ?? '（未知客戶）'}</span>
          <Tag style={{ margin: 0, fontFamily: font.mono, fontSize: text.xs }}>
            #{record.clientId}
          </Tag>
        </span>
      ),
    },
    {
      title: '迴避原因',
      dataIndex: 'reason',
      render: (v: string) => <span style={{ color: color.text.primary }}>{v}</span>,
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 130,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.tertiary }}>
          {formatDate(v)}
        </span>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="編輯">
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={14} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="利益迴避管理"
        description="記錄稽核員與客戶之間的利益衝突關係，確保稽核中立性。"
        meta={
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: text.sm,
              color: color.text.secondary,
            }}
          >
            <ShieldAlert size={13} />
            <span style={{ color: color.text.muted }}>迴避記錄</span>
            <span style={{ fontWeight: weight.semibold, color: color.text.primary }}>
              {total} 筆
            </span>
          </span>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增記錄
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋稽核員 / 客戶 / 迴避原因"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            extra={
              searchText && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => setSearchText('')}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<ConflictOfInterest>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 800 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
          }}
        />

        {/* 序86：技術委員會主委代表授權（審議安排權限名單） */}
        <div style={{ marginTop: 24 }}>
          <ChairDelegatesCard />
        </div>
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={16} color={color.state.warning} />
            {editingCOI ? '編輯利益迴避' : '新增利益迴避'}
          </span>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <ConflictOfInterestForm
          item={editingCOI}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-conflict-of-interest'] });
          }}
          form={form}
        />
      </Modal>
    </>
  );
}

/**
 * 利益迴避新增/編輯表單。
 * 序54：稽核員 → AuditorSelect（員工檔案搜尋勾選）；客戶 → ClientSelect（客戶檔案搜尋勾選）。
 * 編輯態鎖定稽核員/客戶（僅允許改迴避原因，與後端 PATCH 契約一致）。
 */
function ConflictOfInterestForm({
  item,
  onClose,
  form,
}: {
  item: ConflictOfInterest | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm>[0];
}) {
  const qc = useQueryClient();

  /** 新增記錄 Mutation */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/conflict-of-interest', v),
    onSuccess: () => {
      message.success('利益迴避記錄已新增');
      void qc.invalidateQueries({ queryKey: ['settings-conflict-of-interest'] });
      onClose();
    },
  });

  /** 更新記錄 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) =>
      apiClient.patch(`/api/settings/conflict-of-interest/${item!.id}`, v),
    onSuccess: () => {
      message.success('利益迴避記錄已更新');
      void qc.invalidateQueries({ queryKey: ['settings-conflict-of-interest'] });
      onClose();
    },
  });

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ?? {}}
      onFinish={(v) => (item ? update.mutate(v) : create.mutate(v))}
      style={{ marginTop: 16 }}
    >
      {/* 序54：稽核員 / 客戶 關聯選擇器，兩欄並排；編輯態鎖定不可改 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="auditorId"
            label="稽核員"
            rules={[{ required: true, message: '請選擇稽核員' }]}
          >
            <AuditorSelect disabled={!!item} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="clientId"
            label="客戶"
            rules={[{ required: true, message: '請選擇客戶' }]}
          >
            <ClientSelect disabled={!!item} />
          </Form.Item>
        </Col>
      </Row>

      {/* 迴避原因（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="reason" label="迴避原因" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="說明利益衝突原因…" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '新增'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

// ─── 序86：技術委員會主委代表授權卡片 ─────────────────────────────────────────

/** 授權名單項（GET /api/settings/committee-delegates） */
interface ChairDelegate {
  id: number;
  userId: number;
  note: string | null;
  createdAt: string;
  userName: string | null;
  username: string | null;
}

/**
 * 主委代表授權管理（序86）：
 * 「審議安排」僅限本名單內的用戶（admin 恆可，後備通道）。
 * 由主委授權的稽核員為主委代表，或主委代表選定的助理。
 */
function ChairDelegatesCard() {
  const qc = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [note, setNote] = useState('');

  const { data, isLoading } = useQuery<{ items: ChairDelegate[] }>({
    queryKey: ['committee-delegates'],
    queryFn: () => apiClient.get('/api/settings/committee-delegates'),
  });

  const add = useMutation({
    mutationFn: () =>
      apiClient.post('/api/settings/committee-delegates', {
        userId: selectedUserId,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('已授權');
      setSelectedUserId(undefined);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['committee-delegates'] });
    },
    onError: (e: Error) => {
      console.error('[ChairDelegatesCard] add failed', e);
      message.error(e.message || '授權失敗');
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/settings/committee-delegates/${id}`),
    onSuccess: () => {
      message.success('已撤銷授權');
      void qc.invalidateQueries({ queryKey: ['committee-delegates'] });
    },
    onError: (e: Error) => {
      console.error('[ChairDelegatesCard] remove failed', e);
      message.error(e.message || '撤銷失敗');
    },
  });

  const cols: ColumnsType<ChairDelegate> = [
    {
      title: '主委代表',
      dataIndex: 'userName',
      render: (v: string | null, r) => `${v ?? '—'}（${r.username ?? r.userId}）`,
    },
    { title: '備註', dataIndex: 'note', render: (v: string | null) => v ?? '—' },
    {
      title: '授權時間',
      dataIndex: 'createdAt',
      width: 170,
      render: (d: string) => formatDateTime(d),
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" danger onClick={() => remove.mutate(r.id)}>
          撤銷
        </Button>
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #f0f0f0' }}>
      <div style={{ marginBottom: 12 }}>
        <strong>技術委員會主委代表授權（審議安排權限）</strong>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
          序86：驗證決定的「審議安排」僅限本名單內的主委代表（由主委授權的稽核員，或主委代表選定的助理）；
          系統管理員恆可安排（後備通道）。
        </div>
      </div>
      <Space style={{ marginBottom: 12 }} wrap>
        <AuditorSelect
          style={{ width: 260 }}
          placeholder="選擇要授權的員工…"
          value={selectedUserId}
          onChange={(v) => setSelectedUserId(v as number)}
        />
        <Input
          style={{ width: 220 }}
          placeholder="授權備註（選填）"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          type="primary"
          disabled={!selectedUserId}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          授權
        </Button>
      </Space>
      <Table<ChairDelegate>
        rowKey="id"
        size="small"
        columns={cols}
        dataSource={data?.items ?? []}
        loading={isLoading}
        pagination={false}
      />
    </div>
  );
}
